import { format, isSameDay, isSameYear } from 'date-fns';
import { createElement, useCallback, useEffect, useMemo } from 'react';
import { getI18n } from 'react-i18next';

import { BpClassHeader, BpStatus, ITableCell, ITableRow } from '../../components/app';
import { SERVER_DATE_FORMAT } from '../../constants';
import { Device, Direction, RangeType } from '../../enums';
import { IPatient } from '../../interfaces';
import { toUTCDate, useDispatch, useSelector } from '../../utils';
import { useChartMeasurements } from '../chart';
import { usePatientByUrlParam } from '../patients';
import {
  changeDateRange,
  changeRangeType,
  fetchCuffMeasurements,
  fetchPhysiologicalData,
  resetPhysiologicalData,
  resetToDefaultViewAndRange,
} from './actions';
import { PhysiologicalDataTableColumns } from './constants';
import { PhysiologicalDataTableColumn } from './enums';
import { selectPhysiologicalData } from './selectors';
import { getIntervalForRangeType } from './utils';

export const usePhysiologicalData = () => useSelector(selectPhysiologicalData);
export const useDateRange = () => {
  const { currentDate, rangeType } = usePhysiologicalData();
  return useMemo(
    () => ({
      ...getIntervalForRangeType(rangeType, currentDate),
      rangeType,
    }),
    [currentDate, rangeType],
  );
};

export function useResetPhysiologicalData() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetPhysiologicalData());
  }, [dispatch]);
}

export function useChangeDateRange() {
  const dispatch = useDispatch();

  return useCallback(
    (direction: Direction) => {
      dispatch(changeDateRange({ direction }));
    },
    [dispatch],
  );
}

export function useChangeRangeType() {
  const dispatch = useDispatch();

  return useCallback(
    (rangeType: RangeType) => {
      dispatch(changeRangeType({ rangeType }));
    },
    [dispatch],
  );
}

export function useResetToDefaultViewAndRange() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetToDefaultViewAndRange());
  }, [dispatch]);
}

export function useFetchPhysiologicalData(patient?: IPatient) {
  const dispatch = useDispatch();
  const { dateFrom, dateTo, rangeType } = useDateRange();

  useEffect(() => {
    if (!patient) {
      return;
    }

    dispatch(
      fetchPhysiologicalData({
        rangeType,
        patient,
        dateFrom: format(dateFrom, SERVER_DATE_FORMAT),
        dateTo: format(dateTo, SERVER_DATE_FORMAT),
      }),
    );
  }, [dispatch, patient, dateFrom, dateTo, rangeType]);
}

export function useFetchCuffMeasurements(patient?: IPatient) {
  const dispatch = useDispatch();
  const { dateFrom, dateTo, rangeType } = useDateRange();

  useEffect(() => {
    if (!patient) {
      return;
    }

    dispatch(
      fetchCuffMeasurements({
        patientId: patient.id,
        dateFrom: format(dateFrom, SERVER_DATE_FORMAT),
        dateTo: format(dateTo, SERVER_DATE_FORMAT),
      }),
    );
  }, [dispatch, patient, dateFrom, dateTo, rangeType]);
}

export function useDateRangeText() {
  const { dateFrom, dateTo, rangeType } = useDateRange();

  return useMemo(() => {
    if (rangeType === RangeType.DAY) {
      const date = format(dateFrom, 'd MMM yyyy');
      return isSameDay(dateFrom, Date.now()) ? getI18n().t('patient.label.date.today', { date }) : date;
    }

    const from = format(dateFrom, isSameYear(dateFrom, dateTo) ? 'd MMM' : 'd MMM yyyy');
    const to = format(dateTo, 'd MMM yyyy');
    return getI18n().t('patient.label.date.range', { from, to });
  }, [dateFrom, dateTo, rangeType]);
}

export function useTableRows(device: Device): ITableRow[] {
  const patient = usePatientByUrlParam();
  const { cuffMeasurements, rangeType } = usePhysiologicalData();
  const chartMeasurements = useChartMeasurements();

  return useMemo(() => {
    const measurements =
      device === Device.BRACELET
        ? chartMeasurements
        : cuffMeasurements
        ? Object.values(cuffMeasurements.measurements)
        : undefined;
    if (!patient || !measurements) {
      return [];
    }

    return measurements.map((measurement) => {
      const row: ITableRow = {
        id: measurement._id,
        elements: [],
      };

      const formatDate = () => {
        switch (device) {
          case Device.BRACELET:
            return measurement.tooltipText;
          case Device.CUFF:
            return format(toUTCDate(measurement._id), rangeType === RangeType.DAY ? 'h:mmaaa' : 'd MMM yyyy, h:mmaaa');
        }
      };

      const rangeText = formatDate();

      row.elements = PhysiologicalDataTableColumns.map((key) => {
        switch (key) {
          case PhysiologicalDataTableColumn.DATE:
            return createElement('span', {}, rangeText);
          case PhysiologicalDataTableColumn.BP_TYPE:
            return createElement(
              'div',
              { className: 'flex justify-end w-full' },
              measurement.bpType
                ? createElement(BpStatus, {
                    disabled: true,
                    status: measurement.bpType || '',
                    showBackground: false,
                    isUS: patient.countryKey === 'usa',
                  })
                : '-',
            );
          default:
            return createElement('span', {}, measurement.bpType ? (measurement[key] ? measurement[key] : '') : '-');
        }
      }).map((element) => ({
        element,
      }));

      return row;
    });
  }, [cuffMeasurements, chartMeasurements, rangeType, patient]);
}

export function useTableHeaders(device: Device): ITableCell[] {
  const { rangeType } = usePhysiologicalData();

  return useMemo(() => {
    return PhysiologicalDataTableColumns.map((column) => {
      const getLabel = (key: string = column) => getI18n().t(`patient.table.${key}`);
      switch (column) {
        case PhysiologicalDataTableColumn.DATE: {
          const label =
            rangeType === RangeType.DAY
              ? getLabel('time')
              : device === Device.BRACELET
              ? getLabel()
              : getLabel('datetime');
          return createElement('span', {}, label);
        }
        case PhysiologicalDataTableColumn.BP_TYPE:
          return createElement('div', { className: 'flex justify-end w-full' }, createElement(BpClassHeader));
        default:
          return createElement('span', {}, getLabel());
      }
    }).map((element) => ({
      element,
    }));
  }, [rangeType]);
}
